import { Volume2, VolumeX } from "lucide-react";

import type { AnswerReview } from "@/api/tests-api";

/**
 * The candidate's cases, pinned beside the review.
 *
 * WHY IT IS OUTSIDE THE DRAWER RATHER THAN IN IT. The obvious place
 * for a case list is a rail inside the panel, the way the candidate's
 * own sitting has one. There is no room: the drawer is 960px and the
 * reading column inside it is 880 — the application's own width, which
 * is not negotiable because it is the candidate's approved form.
 * Fitting a rail beside it would mean widening the drawer back toward
 * 1240 and carrying two layouts.
 *
 * So it takes the space that is already empty — the backdrop to the
 * left of the drawer, a third of the window on a wide screen — and
 * butts against the drawer's edge rather than floating in it, so the
 * two read as one window in two panes. Fixed, so it holds still while
 * the application scrolls past.
 *
 * IT DISAPPEARS BELOW 1320px rather than overlapping the drawer, and
 * the speaker button falls back to scrolling to the dictations — the
 * behaviour it had before this existed. A rail that sits on top of the
 * thing being read is worse than no rail.
 */
export function CaseRail({
  answers,
  onOpenCase,
  onClose,
}: {
  answers: AnswerReview[];
  onOpenCase: (index: number) => void;
  onClose: () => void;
}) {
  const recorded = answers.filter((a) => a.has_audio).length;

  return (
    <aside data-cy="case-rail" className="sk-case-rail">
      <div className="flex shrink-0 items-baseline justify-between gap-2">
        <h3 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-accent-hover">
          Their dictations
        </h3>
        <button
          type="button"
          data-cy="btn-close-case-rail"
          onClick={onClose}
          className="text-[11px] text-foreground-muted hover:text-foreground"
        >
          hide
        </button>
      </div>
      <p className="mt-0.5 shrink-0 text-[11px] text-foreground-muted">
        {recorded} of {answers.length} recorded
      </p>

      <ul className="sk-case-rail-list mt-3 flex flex-col gap-1">
        {answers.map((a, i) => (
          <li key={a.assignment_case_id}>
            <button
              type="button"
              data-cy="case-rail-item"
              onClick={() => onOpenCase(i)}
              title={`Open case ${i + 1}`}
              className="flex w-full items-center gap-2.5 rounded-md border border-transparent px-2.5 py-2 text-left text-xs text-foreground hover:border-border-strong hover:bg-table-row-hover"
            >
              {/* A SILENT CASE IS THE ONE A REVIEWER IS HUNTING FOR, so
                  it is marked rather than merely unmarked — the same
                  reason the full-screen rail says "silent" instead of
                  leaving the row blank. */}
              {a.has_audio ? (
                <Volume2 aria-hidden className="h-3.5 w-3.5 shrink-0 text-accent-hover" />
              ) : (
                <VolumeX
                  aria-hidden
                  className="h-3.5 w-3.5 shrink-0 text-status-urgent"
                />
              )}
              <span className="min-w-0 flex-1 truncate">
                {a.position + 1}. {a.title}
              </span>
              {a.duration_seconds != null && a.has_audio && (
                <span className="shrink-0 tabular-nums text-[10px] text-foreground-muted">
                  {Math.floor(a.duration_seconds / 60)}:
                  {String(a.duration_seconds % 60).padStart(2, "0")}
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </aside>
  );
}
